/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import lookie from 'lookie';
import dayjs from 'dayjs';
import { useLatest, useMemoizedFn, useMount, useTimeout } from 'ahooks';
import { Icon } from '@fuxi/eevee-icon';
import { Button } from 'antd-mobile';
import { QRCode } from 'antd';
import {
  FrameAnimation,
  PhysicalInfoDimension,
  PhysicalInfoSource,
  RecommendToTherapyFrom,
  TrackerCustomEvent,
  useTracker,
} from 'common-package';
import service from '@/service';
import { ScrollView, Toast, When } from '@/components';
import { ALL_PAGES, MassageType2Text, SELECTED_CUSTOMER_ID, storagePrefixV2 } from '@/constant';
import { useNav } from '@/hooks';
import logoImg from '@/assets/image/home-logo.png';
import { THERAPY_TYPE_2_TEXT } from '../../utils';
import { useAIDiagnoseStore } from '../../store/useAIDiagnoseStore';
import { MARK_BACK_AREA, MARK_SHOULDER_AREA } from '../pain-condition-level';
import { PART_2_LABEL_TEXT } from '../current-dimension';
import { PHYSICAL_CONDITION_ITEMS } from '../physical-condition';
import { PAIN_AREA_CONFIG } from '../mark-pain-area';
import { formateFrequency } from '../share-result-plan';
import { POSTURE_ADJUST_ITEM_2_TEXT, POSTURE_ADJUST_ITEM_2_URL } from '../posture-condition';

import cx from './index.module.scss';

interface Props {}

// 肩颈区
export const SHOULDER_NECK_AREA: AIDiagnose.MarkPainArea[] = ['leftUpperTrapezius', 'rightUpperTrapezius'];

// 背部区域
export const BACK_AREA: AIDiagnose.MarkPainArea[] = [
  'leftLowerTrapezius',
  'rightLowerTrapezius',
  'leftInfraspinatus',
  'rightInfraspinatus',
  'leftBack',
  'rightBack',
];

export const WEIGHT_LOSS_PROBLEM_2_TEXT: Record<AIDiagnose.DimensionType, { typeName: string; dimensionName: string }> =
  {
    waistCircumference: {
      typeName: '腰腹塑形',
      dimensionName: '腰围',
    },
    backThickness: {
      typeName: '轻薄背部',
      dimensionName: '背厚',
    },
    leftArmCircumference: {
      typeName: '打造天鹅臂',
      dimensionName: '左臂',
    },
    rightArmCircumference: {
      typeName: '打造天鹅臂',
      dimensionName: '右臂',
    },
    leftCalfCircumference: {
      typeName: '纤细腿部',
      dimensionName: '左小腿',
    },
    rightCalfCircumference: {
      typeName: '纤细腿部',
      dimensionName: '右小腿',
    },
    leftThighCircumference: {
      typeName: '纤细腿部',
      dimensionName: '左大腿',
    },
    rightThighCircumference: {
      typeName: '纤细腿部',
      dimensionName: '右大腿',
    },
  };

const THERAPY_TYPE_2_LABEL: Record<AIDiagnose.TherapyType, string> = {
  weightLoss: '您的减肥瘦身诉求',
  bodyAdjust: '您想改善的体态问题',
  painRelief: '您的痛症区域',
  physicalImprove: '您的体质',
};

export const ResultPlan: React.FC<Props> = () => {
  const {
    currentDimension,
    targetDimension,
    markPainArea,
    selectTherapyType,
    userInfo,
    selectPart,
    postureConditionLevel,
    painConditionLevel,
    physicalConditionLevel,
    tongueDiagnoseReportId,
  } = useAIDiagnoseStore.use.formValue();
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState('');
  const selectPartLatest = useLatest(selectPart);
  const selectTherapyTypeLatest = useLatest(selectTherapyType);
  const [AIDiagnoseReport, setAIDiagnoseReport] = useState<GetAIDiagnoseResultResponse>({} as any);
  // const [audioUrl, setAudioUrl] = useState('');
  const [canShowResult, setCanShowResult] = useState(false);
  const [isExpandUserInfo, setIsExpandUserInfo] = useState(false);
  const { sendEvent } = useTracker();

  const nav = useNav();

  useTimeout(() => {
    setCanShowResult(true);
  }, 4000);

  // 展示痛症舒缓选中区域
  const currentMarkPainAreaShowName = useMemo(
    () =>
      Array.from(
        new Set(
          AIDiagnoseReport?.painReliefList?.map(item =>
            SHOULDER_NECK_AREA.includes(item.problem) ? '肩部' : BACK_AREA.includes(item?.problem) ? '背部' : '腰部'
          )
        )
      )
        ?.sort((a, b) => ['肩部', '背部', '腰部']?.indexOf(a) - ['肩部', '背部', '腰部']?.indexOf(b))
        ?.join('、') || null,
    [AIDiagnoseReport]
  );

  // 选择的减肥诉求
  const currentWeightLoseRquestion = useMemo(() => {
    let defaultText = Array.from(
      new Set(AIDiagnoseReport?.weightLossList?.map(item => WEIGHT_LOSS_PROBLEM_2_TEXT[item?.problem]?.typeName))
    )?.join('、');

    const isShowExtraText = AIDiagnoseReport?.weightLossList?.some(item => item?.currentValue - item.goalValue >= 60);

    if (isShowExtraText) {
      return (
        <>
          {defaultText}
          <span style={{ color: '#000000D9', fontWeight: 'normal' }}>
            ，目标跨度稍大，需要保持一定的理疗频率加强效果哦～
          </span>
        </>
      );
    }

    return defaultText;
  }, [AIDiagnoseReport]);

  // 建议列表
  const advicesList = useMemo(
    () => [
      {
        title: '拉伸建议',
        text: AIDiagnoseReport?.stretchAdvice,
        images: AIDiagnoseReport?.stretchImages,
      },
      {
        title: '活动建议',
        text: AIDiagnoseReport?.activityAdvice,
        images: AIDiagnoseReport?.activityImages,
      },
      {
        title: '饮食建议',
        text: AIDiagnoseReport?.suggestFoodAdvice,
        images: AIDiagnoseReport?.suggestFoodImages,
      },
      {
        title: '饮食禁忌建议',
        text: AIDiagnoseReport?.forbiddenFoodAdvice,
        images: [],
      },
      {
        title: '睡眠建议',
        text: AIDiagnoseReport?.sleepAdvice,
        images: AIDiagnoseReport?.sleepImages,
      },
      {
        title: '生活习惯建议',
        text: AIDiagnoseReport?.habitAdvice,
        images: AIDiagnoseReport?.habitImages,
      },
      {
        title: '居住环境建议',
        text: AIDiagnoseReport?.livingEnvironmentAdvice,
        images: [],
      },
    ],
    [AIDiagnoseReport]
  );

  const getResult = useMemoizedFn(async () => {
    let weightLossParams = null,
      bodyAdjustParams = null,
      painReliefParams = null,
      physicalImproveParams = null;

    selectTherapyType?.forEach(item => {
      if (item === 'weightLoss') {
        const dimensionKeys = selectPart
          ?.map(item => PART_2_LABEL_TEXT[item]?.partFields?.concat(PART_2_LABEL_TEXT[item]?.extraFields || []))
          ?.flat();

        weightLossParams = dimensionKeys?.map(item => ({
          problem: item,
          currentValue: currentDimension[item] * 10, // 转成毫米
          goalValue: targetDimension[item] * 10,
        }));
      } else if (item === 'bodyAdjust') {
        bodyAdjustParams = Object.keys(postureConditionLevel || [])?.map(item => ({
          problem: item,
          level: postureConditionLevel[item],
        }));
      } else if (item === 'painRelief') {
        painReliefParams = markPainArea?.map(item => {
          const currentPart = MARK_SHOULDER_AREA.includes(item)
            ? 'shoulder'
            : MARK_BACK_AREA.includes(item)
            ? 'back'
            : 'waist';

          return {
            problem: item,
            level: painConditionLevel[currentPart].level,
            duration: painConditionLevel[currentPart].duration,
          };
        });
      } else if (item === 'physicalImprove') {
        physicalImproveParams = Object.keys(physicalConditionLevel || [])?.map(item => ({
          problem: item,
          level: physicalConditionLevel[item],
        }));
      }
    });

    try {
      const { data: sessionId } = await service.AIDiagnose.submit({
        customerId: lookie.get(SELECTED_CUSTOMER_ID),
        gender: userInfo?.gender,
        birthday: dayjs(userInfo?.birthday).format('YYYY-MM-DD'),
        weightLossList: weightLossParams,
        bodyAdjustList: bodyAdjustParams,
        painReliefList: painReliefParams,
        physicalImproveList: physicalImproveParams,
        tongueDiagnoseReportId: tongueDiagnoseReportId?.value,
      });

      setSessionId(sessionId);

      const { data } = await service.AIDiagnose.getAIDiagnoseReport(sessionId);
      setAIDiagnoseReport(data);
      // const { data: audioResponse } = await service.customer.getAIReportAudio(sessionId);
      // audioResponse && setAudioUrl(audioResponse);
    } finally {
      setIsLoading(false);
    }
  });

  const planTitleName = useMemo(() => {
    const selectTherapyTypes = AIDiagnoseReport?.types;
    if (!selectTherapyTypes?.length) return '';

    if (selectTherapyTypes?.length > 1) {
      return '身体综合改善计划';
    } else {
      return THERAPY_TYPE_2_TEXT[selectTherapyTypes?.[0]] + '计划';
    }
  }, [AIDiagnoseReport?.types]);

  const handleOneMoreOrder = useCallback(
    async (params: { storeGoodsId: number; massageItem: Massage.MassageItem; bodyPart: Service.BodyPart }) => {
      const { storeGoodsId, massageItem, bodyPart } = params || {};

      const { data } = await service.service.checkStoreGoods(storeGoodsId);
      if (!!data) {
        sendEvent?.(TrackerCustomEvent.RecommendToTherapy, {
          et: 'CLK',
          c1: RecommendToTherapyFrom.AiConsultationResult,
        });

        nav(ALL_PAGES.ADD_SERVICE, {
          state: {
            massageItem,
            bodyPart,
            storeGoodsId,
            from: 'ai-consultation',
          },
        });
      } else {
        Toast.showVScreen('该方案已下架，请选择其它方案');
      }
    },
    [nav, sendEvent]
  );

  /** 发送AI问诊填写记录埋点 */
  const sendRecord = useCallback(() => {
    const sendRecordEvent = (c1: string) => {
      sendEvent(TrackerCustomEvent.FillInPhysicalInfoSource, {
        et: 'CLK',
        c1,
        c2: PhysicalInfoSource['ai-consultation'],
      });
    };

    if (selectTherapyTypeLatest.current?.includes('weightLoss')) {
      const shouldSendItems = selectPartLatest.current?.reduce((accur, cur) => {
        accur.push(...(PART_2_LABEL_TEXT[cur].partFields || []));

        if (PART_2_LABEL_TEXT[cur].extraFields?.length) {
          accur.push(...(PART_2_LABEL_TEXT[cur].extraFields || []));
        }

        return accur;
      }, []);

      shouldSendItems.forEach(item => sendRecordEvent(PhysicalInfoDimension[item]));
    }

    if (selectTherapyTypeLatest.current?.includes('painRelief')) {
      sendRecordEvent(PhysicalInfoDimension['painLevel']);
    }
    if (selectTherapyTypeLatest.current?.includes('physicalImprove')) {
      sendRecordEvent(PhysicalInfoDimension['physicalLevel']);
    }
  }, [selectTherapyTypeLatest, selectPartLatest, sendEvent]);

  useMount(() => {
    getResult();
  });

  useEffect(() => {
    if (sendEvent) {
      sendRecord();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEvent]);

  return (
    <>
      {!canShowResult || isLoading ? (
        <div className={cx('loading-wrapper')}>
          <div className={cx('animation-wrapper')}>
            <FrameAnimation
              aspectRatio={0.912}
              count={169}
              width={400}
              duration={6}
              url={storagePrefixV2 + '/ai-consulation/ai-result-loading.png'}
            />
            <div className={cx('loading-text')}>正在为您生成专属理疗报告...</div>
          </div>
        </div>
      ) : (
        <ScrollView autoRefresh className={cx('ResultPlan-wrapper')}>
          <div className={cx('ResultPlan')}>
            {/* <audio src={audioUrl} autoPlay className={cx('audio')} /> */}
            <div className={cx('bg-wrapper')} />
            <div className={cx('header')}>
              <img className={cx('netease-logo')} src={logoImg} alt="" />
              <span className={cx('user-tips')}>
                Hi，{AIDiagnoseReport?.customerName}，{AIDiagnoseReport?.storeName}已为您制定
              </span>
              <span className={cx('plan-name')}>{planTitleName}</span>
              <div className={cx('plan-tips')}>
                <span>您的理疗诉求</span>
                {AIDiagnoseReport?.types?.map(item => (
                  <span className={cx('therapy-type-item')} key={item}>
                    {THERAPY_TYPE_2_TEXT[item]}
                  </span>
                ))}
                <div className={cx('control-show')} onClick={() => setIsExpandUserInfo(s => !s)}>
                  <span>{isExpandUserInfo ? '收起' : '展开'}</span>
                  <Icon name={isExpandUserInfo ? 'arrow-up' : 'arrow-down'} size={30} />
                </div>
              </div>
            </div>
            <div className={cx('content')}>
              <div className={cx('select-therapy-list', { show: isExpandUserInfo })}>
                {AIDiagnoseReport?.types
                  // 排序
                  ?.sort(
                    (a, b) =>
                      Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(a) - Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(b)
                  )
                  ?.map(item => (
                    <div key={item}>
                      <div className={cx('header-with-border')}>
                        <span className={cx('therapy-detail')}>
                          <div className={cx('inner-box')}>
                            <span className={cx('label')}>{THERAPY_TYPE_2_LABEL[item]}：</span>
                            <span>
                              {item === 'painRelief' ? (
                                <>{currentMarkPainAreaShowName}</>
                              ) : item === 'bodyAdjust' ? (
                                <>
                                  {AIDiagnoseReport?.bodyAdjustList
                                    ?.sort(
                                      (a, b) =>
                                        Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
                                        Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
                                    )
                                    ?.map(item => POSTURE_ADJUST_ITEM_2_TEXT[item?.problem])
                                    ?.join('、')}
                                </>
                              ) : item === 'weightLoss' ? (
                                <>{currentWeightLoseRquestion}</>
                              ) : item === 'physicalImprove' ? (
                                <>{AIDiagnoseReport?.physiqueName}</>
                              ) : null}
                            </span>
                          </div>
                        </span>
                      </div>
                      <div className={cx('therapy-extra-info')}>
                        {item === 'painRelief' ? (
                          <div className={cx('pain-area-container')}>
                            <div className={cx('pain-area-wrapper')}>
                              {Object.keys(PAIN_AREA_CONFIG).map(item => (
                                <div
                                  className={cx('marker-item', item)}
                                  key={item}
                                  style={{
                                    backgroundImage: `url(${
                                      PAIN_AREA_CONFIG[item as AIDiagnose.MarkPainArea]?.[
                                        AIDiagnoseReport?.painReliefList?.some(item2 => item2?.problem === item)
                                          ? 'selectUrl'
                                          : 'defaultPlainUrl'
                                      ]
                                    })`,
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        ) : item === 'bodyAdjust' ? (
                          <div className={cx('body-adjust-wrapper')}>
                            {AIDiagnoseReport?.bodyAdjustList
                              ?.sort(
                                (a, b) =>
                                  Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
                                  Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
                              )
                              ?.map(item => (
                                <div
                                  className={cx('image-item')}
                                  key={item.problem}
                                  style={{ backgroundImage: `url(${POSTURE_ADJUST_ITEM_2_URL[item?.problem]})` }}
                                ></div>
                              ))}
                          </div>
                        ) : item === 'weightLoss' ? (
                          <div className={cx('weight-loss-wrapper')}>
                            {AIDiagnoseReport?.weightLossList // 表单排序
                              ?.sort(
                                (a, b) =>
                                  Object.keys(WEIGHT_LOSS_PROBLEM_2_TEXT)?.indexOf(a?.problem) -
                                  Object.keys(WEIGHT_LOSS_PROBLEM_2_TEXT)?.indexOf(b?.problem)
                              )
                              ?.map(item => (
                                <div className={cx('weightloss-infos-item')} key={item?.problem}>
                                  <div className={cx('left')}>
                                    <span>当前</span>
                                    <span className={cx('highlight')}>{item.currentValue / 10}</span>
                                    <span>厘米</span>
                                  </div>
                                  <div className={cx('part-type-name')}>
                                    {WEIGHT_LOSS_PROBLEM_2_TEXT[item?.problem]?.dimensionName}
                                  </div>
                                  <div className={cx('right')}>
                                    <span>目标</span>
                                    <span className={cx('highlight')}>{item.goalValue / 10}</span>
                                    <span>厘米</span>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : item === 'physicalImprove' ? (
                          <div className={cx('physical-wrapper')}>
                            <div className={cx('physical-analyse')}>{AIDiagnoseReport?.physiqueAnalysis}</div>
                            <div>
                              <span>您希望改善的体质问题是：</span>
                              <span className={cx('highlight')}>
                                {AIDiagnoseReport?.physicalImproveList
                                  ?.sort(
                                    (a, b) =>
                                      Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(a?.problem) -
                                      Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(b?.problem)
                                  )
                                  ?.map(item => PHYSICAL_CONDITION_ITEMS[item?.problem]?.name)
                                  ?.filter(Boolean)
                                  ?.join('、')}
                              </span>
                            </div>
                            <When condition={!!AIDiagnoseReport?.physiqueAnalysis}>
                              <div className={cx('duty-tips')}>
                                <Icon className={cx('icon')} name="info-circle" />
                                <div>舌诊是中医四诊的部分内容，上述诊断结果仅供参考，不能作为诊断治疗依据</div>
                              </div>
                            </When>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>

              <div className={cx('item-header', 'show-area-2')}>我的专属方案包</div>
              {!!AIDiagnoseReport?.recommendGoodsList?.length ? (
                <>
                  <div className={cx('recommend-list', 'show-area-2')}>
                    {AIDiagnoseReport?.recommendGoodsList?.map(item => (
                      <div className={cx('plan-details-item')} key={item.id}>
                        <div className={cx('plan-detail-left')}>
                          <div
                            className={cx('img-box', item?.bodyPart)}
                            style={{
                              backgroundImage: `url(${item?.wxCoverImage})`,
                            }}
                          >
                            <div className={cx('color-box')}></div>
                            <div className={cx('header-box')}>
                              <div className={cx('recommend-plan-header')}>
                                <div className={cx('name', { 'is-draft': false })}>{item.name}</div>

                                <div className={cx('header-tag')}>
                                  <div className={cx(item.itemType + '-tags', 'type')}>
                                    {MassageType2Text[item.itemType]}
                                  </div>
                                </div>
                              </div>

                              {/* 人工套餐无部位区分 */}
                              {false ? (
                                <div className={cx('only-manual')}>
                                  <Icon name="only-hand" className={cx('icon')} size="12px" />
                                  <span>仅支持人工操作</span>
                                </div>
                              ) : (
                                item?.recommendedTime && (
                                  <div className={cx('time')}>{`${item.recommendedTime}分钟`}</div>
                                )
                              )}
                              {/* 人工套餐无部位区分 */}
                            </div>
                          </div>
                        </div>
                        <div className={cx('plan-detail-right')}>
                          <div className={cx('intros')}>{item.effectIntro}</div>
                          <div className={cx('aim-type')}>
                            <div className={cx('aim-type-label')}>针对</div>
                            <div className={cx('aim-type-value')}>
                              {item?.types
                                ?.sort(
                                  (a, b) =>
                                    Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(a) -
                                    Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(b)
                                )
                                ?.map(item => THERAPY_TYPE_2_TEXT[item])
                                ?.join(' · ')}
                            </div>
                          </div>
                          {item.itemType === 'Moxibustion' && (
                            <div className={cx('recommand-tip')}>
                              <div className={cx('label')}>艾灸穴位：</div>
                              <div className={cx('value', 'highlight')}>{item.acupoints?.join(' ')}</div>
                            </div>
                          )}
                          <div className={cx('recommand-tip')}>
                            <div className={cx('label')}>建议理疗疗程：</div>
                            <div className={cx('value')}>
                              <span className={cx('highlight')}>{item.treatmentCourse}</span>
                              <span>个疗程</span>
                              <span className={cx('highlight')}>{item.treatmentCourse * 10}</span>
                              <span>次以上</span>
                            </div>
                          </div>
                          <div className={cx('recommand-tip')}>
                            <div className={cx('label')}>建议理疗频率：</div>
                            <div className={cx('value')}>
                              <span className={cx('highlight')}>1</span>
                              <span>周</span>
                              <span className={cx('highlight')}>{formateFrequency(item.frequency)}</span>
                              <span>次</span>
                            </div>
                          </div>
                          <div className={cx('btn-wrapper')}>
                            <Button
                              color="primary"
                              className={cx('btn')}
                              onClick={() => {
                                handleOneMoreOrder({
                                  storeGoodsId: item.id,
                                  bodyPart: item.bodyPart,
                                  massageItem: item.itemType,
                                });
                              }}
                            >
                              去理疗
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={cx('therapy-tips', 'show-area-2')}>
                    <div>
                      <span>如下情况不建议进行理疗，请及时与理疗师沟通</span>
                    </div>
                    <div className={cx('tips-list-item')}>
                      <div className={cx('circle')} />
                      <span>体内有金属者、佩戴金属饰品、严重骨质疏松患者</span>
                    </div>
                    <div className={cx('tips-list-item')}>
                      <div className={cx('circle')} />
                      <span>局部有皮肤破损、心血管疾病患者</span>
                    </div>
                    <div className={cx('tips-list-item')}>
                      <div className={cx('circle')} />
                      <span>孕妇与生理期、哺乳期女性</span>
                    </div>
                    <div className={cx('tips-list-item')}>
                      <div className={cx('circle')} />
                      <span>空腹、醉酒等状态下不宜使用</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={cx('empty-plan')}>理疗师将与您亲自沟通理疗方案~</div>
              )}
              <div className={cx('item-header', 'show-area-3')}>日常小建议</div>
              <div className={cx('show-area-3')}>
                {advicesList
                  ?.filter(item => item?.text)
                  ?.map((item, index) => (
                    <div className={cx('advice-wrapper')} key={index}>
                      <div className={cx('header-with-border')}>
                        {item.title}：{item?.text}
                      </div>
                      {!!item?.images?.length && (
                        <div className={cx('food-images')}>
                          {item?.images?.map(item => (
                            <div className={cx('food-image-item')} key={item}>
                              <img src={item} alt="" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
            <div className={cx('footer')}>
              <div className={cx('scan-code')}>
                <QRCode
                  bordered={false}
                  value={`${window?.location?.origin}/share/ai-consultation?session=${sessionId}`}
                />
              </div>
              <div className={cx('scan-tip')}>
                <div>微信扫一扫</div>
                <div>带走您的专属方案</div>
              </div>
            </div>
          </div>
        </ScrollView>
      )}
    </>
  );
};
